import { Builder } from '@builder.io/react';
import cn from 'classnames';
import parse from 'html-react-parser';
import { FC, useState } from 'react';

import { Button } from '@catawiki/cata-style';

import { COLOR_NAME_WHITE } from '@/shared/constants/colors';
import {
  actionButtonInput,
  backgroundColorInput,
  componentLinkIdInput,
} from '@/shared/inputs';
import { getBuilderSpacingClass } from '@/shared/spacing';
import {
  SanitizeMode,
  cleanBuilderParagraphInput,
  cleanBuilderStringInput,
  getBuilderString,
  getCleanInput,
  isBuilderStringPresent,
} from '@/shared/text';
import { BuilderString, SharedBuilderComponent } from '@/shared/types';

import { ActionVariant, BaseAction, getActionVariantName } from '../Action';
import { ComponentHeader } from '../ComponentHeader';
import sharedStyle from '../shared.module.scss';
import { AccordionItemTitle } from './AccordionItemTitle';
import { ExpandableContent } from './ExpandableContent';
import styles from './FAQ.module.scss';
import { FAQContent } from './types';

interface BuilderInput extends SharedBuilderComponent {
  componentLinkId?: string;
  title: BuilderString;
  description: BuilderString;
  content: FAQContent[];
  notes?: BuilderString;

  action: BaseAction;
  actionVariant?: ActionVariant;
}

type Props = BuilderInput & {
  locale: string;
};

export const FAQ: FC<Props> = ({
  componentLinkId,
  title,
  description,
  content = [],
  notes,

  action,
  actionVariant = ActionVariant.NONE,

  locale,
  attributes,
  backgroundColorClassName,
  builderBlock,
  builderState,
}) => {
  const spacingStyles = getBuilderSpacingClass(builderBlock, builderState);
  // When open page in editor or LivePreview mode, Builder doesn't run dataFetcher
  // causing locale value undefined. So, we use builderState locale value when
  // using component in editor and leave the locale when we generate static content
  const blockLocale = locale || builderState?.context.locale;

  const [openedQuestion, setOpened] = useState(-1);
  const toggle = (q: number) => () => setOpened(openedQuestion === q ? -1 : q);

  const notesPresent = isBuilderStringPresent(notes, blockLocale);
  return (
    <section
      {...attributes}
      id={componentLinkId}
      className={cn(
        sharedStyle.section,
        backgroundColorClassName,
        spacingStyles
      )}
    >
      <div className={cn('u-wrapper')}>
        <ComponentHeader
          title={title}
          description={description}
          locale={blockLocale}
          action={action}
          actionVariant={actionVariant}
          headerClassName={cn('u-m-b-lg-xxl-10 u-m-b-md-xxl-6 u-m-b-sm-xxl-6')}
        >
          <>
            <div
              className={cn({
                'u-m-b-lg-xxl-10 u-m-b-md-xxl-6 u-m-b-sm-xxl-6': notesPresent,
              })}
            >
              {content.map((c, idx) => {
                const showButton = c.actionVariant === 'tertiary';
                const lastItem = idx + 1 === content.length;
                const accTitle = cleanBuilderStringInput(
                  getBuilderString(c.title, blockLocale)
                );
                const accContent = cleanBuilderParagraphInput(
                  getBuilderString(c.content, blockLocale)
                );
                const key = idx;

                return (
                  <section
                    key={key}
                    className={cn({
                      'u-border-top': true,
                      'u-border-bottom': lastItem,
                    })}
                  >
                    <AccordionItemTitle
                      open={openedQuestion === idx}
                      onToggle={toggle(idx)}
                    >
                      <div
                        className={cn('u-typography-h5', styles.questionTitle)}
                      >
                        {accTitle && parse(accTitle)}
                      </div>
                    </AccordionItemTitle>
                    <ExpandableContent isOpen={openedQuestion === idx}>
                      <div className="u-p-h-xl u-p-b-xl">
                        {accContent && <div>{parse(accContent)}</div>}

                        {showButton && (
                          <Button
                            variant="tertiary"
                            className="u-m-t-xl"
                            as="a"
                            target="_blank"
                            href={cleanBuilderStringInput(
                              getBuilderString(c.actionUrl, blockLocale),
                              true
                            )}
                          >
                            {parse(
                              cleanBuilderStringInput(
                                getBuilderString(c.actionLabel, blockLocale),
                                true
                              )
                            )}
                          </Button>
                        )}
                      </div>
                    </ExpandableContent>
                  </section>
                );
              })}
              {content.length === 0 && (
                <section className={cn('u-border-top', 'u-border-bottom')}>
                  <AccordionItemTitle
                    open={openedQuestion === 0}
                    onToggle={toggle(0)}
                  >
                    <div
                      className={cn('u-typography-h5', styles.questionTitle)}
                    >
                      This is sample content, please add content.
                    </div>
                  </AccordionItemTitle>
                  <ExpandableContent isOpen={openedQuestion === 0}>
                    <div className="u-p-h-xl u-p-b-xl">
                      <div>
                        This item is showing when content is empty to indicate
                        what is this component suppose to be. Please add an item
                        to the component to remove this item.
                      </div>
                    </div>
                  </ExpandableContent>
                </section>
              )}
            </div>

            {notesPresent && (
              <div className="tw:text-neutral-secondary">
                {parse(
                  getCleanInput(blockLocale, SanitizeMode.MULTIPLE_LINE, notes)
                )}
              </div>
            )}
          </>
        </ComponentHeader>
      </div>
    </section>
  );
};

export function dataFetcher(locale: string) {
  return async () => ({
    locale,
  });
}

export const NAME = 'FAQ';

export function componentRegister() {
  Builder.registerComponent(FAQ, {
    name: NAME,
    inputs: [
      componentLinkIdInput(),
      {
        name: 'title',
        friendlyName: 'Title',
        type: 'CatawikiEditor',
        defaultValue:
          'Title <span class="u-typography-h2-serif tw:text-brand-primary">Text</span>',
      },
      {
        name: 'description',
        friendlyName: 'Description',
        type: 'CatawikiEditor',
        defaultValue: 'Description example',
      },
      ...actionButtonInput({
        allowVariants: [ActionVariant.NONE, ActionVariant.TERTIARY],
      }),
      {
        name: 'notes',
        friendlyName: 'Notes',
        type: 'CatawikiEditor',
        defaultValue: 'Notes example',
      },
      {
        name: 'content',
        friendlyName: 'Content',
        type: 'list',
        subFields: [
          {
            name: 'title',
            friendlyName: 'Question',
            type: 'CatawikiEditor',
            required: true,
          },
          {
            name: 'content',
            friendlyName: 'Answer',
            type: 'CatawikiEditor',
            required: true,
          },
          {
            name: 'actionVariant',
            type: 'string',
            enum: [
              ActionVariant.PRIMARY,
              ActionVariant.SECONDARY,
              ActionVariant.TERTIARY,
            ].map((name) => ({
              label: getActionVariantName(name),
              value: name,
            })),
          },
          {
            name: 'actionLabel',
            friendlyName: 'CTA label',
            type: 'CatawikiEditor',
          },
          {
            name: 'actionUrl',
            friendlyName: 'CTA url',
            type: 'CatawikiPlainEditor',
          },
        ],
      },
      backgroundColorInput(COLOR_NAME_WHITE),
    ],
    noWrap: true,
  });
  Builder.registerComponent(FAQ, {
    name: `v2:${NAME}`,
    inputs: [
      componentLinkIdInput(),
      {
        name: 'title',
        friendlyName: 'Title',
        type: 'html',
        defaultValue:
          'Title <span class="u-typography-h2-serif tw:text-brand-primary">Text</span>',
      },
      {
        name: 'description',
        friendlyName: 'Description',
        type: 'html',
        defaultValue: 'Description example',
      },
      ...actionButtonInput({
        allowVariants: [ActionVariant.NONE, ActionVariant.TERTIARY],
        useCatawikiPlainEditor: false,
      }),
      {
        name: 'notes',
        friendlyName: 'Notes',
        type: 'html',
        defaultValue: 'Notes example',
      },
      {
        name: 'content',
        friendlyName: 'Content',
        type: 'list',
        subFields: [
          {
            name: 'title',
            friendlyName: 'Question',
            type: 'html',
            required: true,
          },
          {
            name: 'content',
            friendlyName: 'Answer',
            type: 'html',
            required: true,
          },
          {
            name: 'actionVariant',
            type: 'string',
            enum: [
              ActionVariant.PRIMARY,
              ActionVariant.SECONDARY,
              ActionVariant.TERTIARY,
            ].map((name) => ({
              label: getActionVariantName(name),
              value: name,
            })),
          },
          {
            name: 'actionLabel',
            friendlyName: 'CTA label',
            type: 'html',
          },
          {
            name: 'actionUrl',
            friendlyName: 'CTA url',
            type: 'CatawikiPlainEditorWithoutBuiltInTranslation',
          },
        ],
      },
      backgroundColorInput(COLOR_NAME_WHITE),
    ],
    noWrap: true,
  });
  return NAME;
}

export const componentDataFetchers = (locale: string) => ({
  [NAME]: dataFetcher(locale),
  [`v2:${NAME}`]: dataFetcher(locale),
});
